import { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { IssueResolverType, MessageV2DTO } from 'support/types';
import { AlertSection } from 'components/Display/Alert';
import { Translate } from 'components/Translate/Translate';

import { ContentBanner } from './ContentBanner';

type GenericBlockingErrorBannerProps = {
  blockingError: NonNullable<MessageV2DTO['blocking_error']>;
  issueResolver?: IssueResolverType;
  isCurrentPartnerReceiver?: boolean;
};

export const GenericBlockingErrorBanner = ({
  blockingError,
  issueResolver,
  isCurrentPartnerReceiver,
}: GenericBlockingErrorBannerProps) => {
  const { t } = useTranslation();

  const severity = issueResolver === IssueResolverType.CURRENT_PARTNER ? 'error' : 'neutral';

  if (issueResolver === IssueResolverType.PROCUROS) {
    return <ProcurosErrorContent blockingError={blockingError} />;
  }

  if (issueResolver === IssueResolverType.OTHER_PARTNER) {
    return <OtherPartnerErrorContent isCurrentPartnerReceiver={isCurrentPartnerReceiver} />;
  }

  return (
    <ContentBanner severity={severity} withWrapper={false}>
      <AlertSection severity={severity} title={blockingError.title}>
        {blockingError.description ? <Translate i18nKey={blockingError.description} /> : null}
      </AlertSection>
      <AlertSection severity={severity} title={t('common:blockingError.howToFix')}>
        {blockingError.howToFix ? <Translate i18nKey={blockingError.howToFix} /> : null}
      </AlertSection>
    </ContentBanner>
  );
};

const ProcurosErrorContent = ({ blockingError }: { blockingError: NonNullable<MessageV2DTO['blocking_error']> }) => {
  const { t, i18n } = useTranslation();

  const code = blockingError.code;
  let title: string | null = blockingError.title || null;
  let description: ReactNode = blockingError.description ? <Translate i18nKey={blockingError.description} /> : null;
  let howToFix: ReactNode = blockingError.howToFix ? <Translate i18nKey={blockingError.howToFix} /> : null;

  if (code) {
    const titleKey = `webedi:viewDocument.procurosIsIssueResolver.${code}.title`;
    const descriptionKey = `webedi:viewDocument.procurosIsIssueResolver.${code}.description`;
    const howToFixKey = `webedi:viewDocument.procurosIsIssueResolver.${code}.howToFix`;

    title = i18n.exists(titleKey) ? t(titleKey) : title;
    description = i18n.exists(descriptionKey) ? (
      <>
        <span>{t(descriptionKey)}</span>
        <br />
      </>
    ) : (
      description
    );
    howToFix = i18n.exists(howToFixKey) ? t(howToFixKey) : howToFix;
  }

  return (
    <ContentBanner severity="neutral" withWrapper={false}>
      <AlertSection severity="neutral" title={title}>
        {description}
        {howToFix}
      </AlertSection>
    </ContentBanner>
  );
};

const OtherPartnerErrorContent = ({ isCurrentPartnerReceiver }: { isCurrentPartnerReceiver?: boolean }) => {
  const { t } = useTranslation();

  const partnerRoleInMessage = isCurrentPartnerReceiver ? 'receiver' : 'sender';

  return (
    <ContentBanner severity="neutral" withWrapper={false}>
      <AlertSection
        severity="neutral"
        title={t(`webedi:viewDocument.${partnerRoleInMessage}.noActionRequiredBanner.title`)}
      >
        {t(`webedi:viewDocument.${partnerRoleInMessage}.noActionRequiredBanner.description`)}
      </AlertSection>
    </ContentBanner>
  );
};

/**
 * i18n keys
 * t('common:blockingError.howToFix')
 * t('webedi:viewDocument.procurosIsIssueResolver.1724421292.title')
 * t('webedi:viewDocument.procurosIsIssueResolver.1724421292.description')
 * t('webedi:viewDocument.procurosIsIssueResolver.1724421292.howToFix')
 * t('webedi:viewDocument.sender.noActionRequiredBanner.title')
 * t('webedi:viewDocument.sender.noActionRequiredBanner.description')
 * t('webedi:viewDocument.receiver.noActionRequiredBanner.title')
 * t('webedi:viewDocument.receiver.noActionRequiredBanner.description')
 */
